/**
 * Utility functions for generating Google Maps and Google Places links
 */

interface PlaceLinks {
  googleMaps: string;
  googlePlaces: string;
  directions: string;
  streetView: string;
  searchQuery: string;
}

/**
 * Generate a Google Maps link using place_id
 * @param placeId - The Google Places place_id
 * @returns Google Maps URL
 */
export const generateGoogleMapsLink = (placeId: string): string => {
  if (!placeId) return '';
  return `https://www.google.com/maps/place/?q=place_id:${placeId}`;
};

/**
 * Generate a Google Places search link using place_id and query
 * @param placeId - The Google Places place_id
 * @param query - Optional search query
 * @returns Google Places search URL
 */
export const generateGooglePlacesLink = (placeId: string, query: string = ''): string => {
  if (!placeId) return '';
  const baseUrl = 'https://www.google.com/maps/search/?api=1';
  const params = new URLSearchParams();
  params.append('query_place_id', placeId);
  if (query) {
    params.append('query', query);
  }
  return `${baseUrl}&${params.toString()}`;
};

/**
 * Generate a Google Maps directions link to a place
 * @param placeId - The Google Places place_id for destination
 * @param origin - Optional origin address or coordinates
 * @returns Google Maps directions URL
 */
export const generateDirectionsLink = (placeId: string, origin: string = ''): string => {
  if (!placeId) return '';
  const baseUrl = 'https://www.google.com/maps/dir/?api=1';
  const params = new URLSearchParams();

  if (origin) {
    params.append('origin', origin);
  }
  // According to Google Maps URLs documentation, when using destination_place_id,
  // you must also include a destination parameter
  params.append('destination', 'place'); // Placeholder destination text
  params.append('destination_place_id', placeId);

  return `${baseUrl}&${params.toString()}`;
};

/**
 * Generate a Google Street View link for a place
 * @param coordinates - [longitude, latitude] array
 * @returns Google Street View URL
 */
export const generateStreetViewLink = (coordinates: [number, number] | null | undefined): string => {
  if (!coordinates || coordinates.length !== 2) return '';
  const [lng, lat] = coordinates;
  return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`;
};

/**
 * Generate multiple link options for a place
 * @param placeId - The Google Places place_id
 * @param coordinates - [longitude, latitude] array
 * @param address - The formatted address
 * @returns Object containing various link types
 */
export const generatePlaceLinks = (placeId: string, coordinates: [number, number] | null | undefined, address: string): PlaceLinks => {
  return {
    googleMaps: generateGoogleMapsLink(placeId),
    googlePlaces: generateGooglePlacesLink(placeId, address),
    directions: generateDirectionsLink(placeId),
    streetView: generateStreetViewLink(coordinates),
    searchQuery: address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : ''
  };
};
